/** 空的缓存键值前缀 */
const EMPTY_CACHE_KEY_PREFIX = 'EMPTY_CACHE_KEY_PREFIX'

/** 服务端缓存的货币，如果有改动，请更新 */
const getCacheCurrencyList = (() => {
  let data: Set<string> | undefined = void 0
  return () => {
    if (!data) {
      const cacheCurncyList = isTest()
        ? ['USD', 'EUR', 'CAD', 'TWD', 'HKD', 'IDR', 'THB', 'VND']
        : [
            'USD',
            'AUD',
            'GBP',
            'EUR',
            'CAD',
            'TWD',
            'HKD',
            'JPY',
            'KRW',
            'MYR',
            'SGD',
            'PHP',
            'IDR',
            'THB',
            'VND',
            'BRL',
          ]
      data = new Set(cacheCurncyList)
    }
    return data
  }
})()

/** csr阶段，判断服务端缓存根据的硬编码货币列表是否发生了改动，如果改动了，请更新CACHE_CURRENCY_LIST */
export function judgeCacheCurrencyListForCSR(list: string[]) {
  if (process.server) {
    return
  }
  const cacheCurrencyList = getCacheCurrencyList()
  const errorTip = `cacheCurrencyList changed`
  const isLengthChange = list.length !== cacheCurrencyList.size
  if (isLengthChange) {
    return console.error(errorTip)
  }
  const isNoChange = list.every(item => cacheCurrencyList.has(item))
  if (!isNoChange) {
    return console.error(errorTip)
  }
}

// 获取用户cookie
function getUserCookie() {
  return useCookie('user') as Ref<any>
}

/** 是游客 */
function getIsTourists(userRef: Ref<any>) {
  try {
    const userId = userRef.value?.userInfo?.userId
    if (userId) {
      return false
    }
    return true
  }
  catch (err) {
    return false
  }
}

/** 游客版的useDataCache，未登录才跑 */
export async function useTouristsDataCache<T>(...args: Parameters<typeof useDataCache>) {
  const [key, ...rest] = args
  const { userLocale: langRef } = storeToRefs(userStore())
  const env = useRuntimeConfig()?.public?.env
  const cacheKeyPrefix = getCacheKeyPrefix(langRef, env)
  const isEmptyCacheKeyPrefix = getIsEmptyCacheKeyPrefix(cacheKeyPrefix)

  // 非法前缀，直接返回空
  if (isEmptyCacheKeyPrefix) {
    return {
      value: void 0,
      addToCache: () => {},
    }
  }

  const userRef = getUserCookie()
  const { value, addToCache, ...res } = await useDataCache<T>(`${cacheKeyPrefix}_${key}`, ...rest)

  const addToCacheWithTourists: (...args: Parameters<typeof addToCache>) => Promise<void> = async (...args) => {
    if (verify({
      userRef,
      langRef,
      cacheKeyPrefix,
      env,
    })) {
      return addToCache(...args)
    }
  }
  // 异步获取数据之后，要cacheKey前缀没有变化 + 游客，才返回对应的数据
  const valueWithTourists: T | undefined = verify({
    userRef,
    langRef,
    cacheKeyPrefix,
    env,
  })
    ? value
    : void 0
  return {
    ...res,
    value: valueWithTourists,
    addToCache: addToCacheWithTourists,
  }
}

/** 是否通过合法校验，必须满足：cacheKey前缀没变化 + 游客 */
function verify({
  userRef,
  langRef,
  cacheKeyPrefix,
  env,
}: { userRef: Ref<any>, langRef: Ref<any>, cacheKeyPrefix: string, env: string }) {
  const isTourists = getIsTourists(userRef)
  const isKeyPrefixChanged = getIsKeyPrefixChanged({
    cacheKeyPrefix,
    langRef,
    env,
  })
  return isTourists && !isKeyPrefixChanged
}

/** cacheKey前缀是否发生变化 */
function getIsKeyPrefixChanged({
  cacheKeyPrefix,
  langRef,
  env,
}: { cacheKeyPrefix: string, langRef: Ref<any>, env: string }) {
  return getCacheKeyPrefix(langRef, env) !== cacheKeyPrefix
}

/** 获取cacheKey前缀 */
export function getCacheKeyPrefix(langRef: Ref<any>, env: string) {
  try {
    const lang = langRef.value || 'en'
    const currentCurrency = storeToRefs(useCurrencyStore())?.currentCurrency?.value
    const cacheCurrencyList = getCacheCurrencyList()
    const currency = cacheCurrencyList.has(currentCurrency) ? currentCurrency : ''
    if (!currency || !lang || !env) {
      return EMPTY_CACHE_KEY_PREFIX
    }
    return `${currency}_${lang}_${env}`
  }
  catch (err) {
    console.log(`getCacheKeyPrefix-err`, err)
    return EMPTY_CACHE_KEY_PREFIX
  }
}

/** 获取cacheKey前缀是否为空的布尔值 */
export function getIsEmptyCacheKeyPrefix(cacheKeyPrefix: string) {
  return cacheKeyPrefix === EMPTY_CACHE_KEY_PREFIX
}
